import {
  EXCLUDED_VARIATION_UUIDS,
  REGISTRY_DOMAIN_UUIDS,
} from '../../shared/constants/variation-uuids.constant';
import { MindOrderExtras } from '../models/order.model';

import { isRegistryDomainProductUuid } from './cart-domain-suggestion-rules';

export type OrderPaymentRecurrency = 'monthly' | 'annual' | string | null | undefined;

export interface OrderRecurrencyPriceItem {
  price: number;
  quantity?: number;
  variationUuid?: string | null;
  /** UUID do produto pai. Registro de domínio usa REGISTRY_DOMAIN_PRODUCT_UUIDS. */
  productUuid?: string | null;
  paymentRecurrency?: OrderPaymentRecurrency;
}

export interface MindOrderListPriceUi {
  displayPrice: number;
  originalPrice: number;
  hasPromotion: boolean;
  promotionMonths: number;
  description: string | null;
}

export function isFreeOrderPrice(price: number): boolean {
  return Math.abs(Number(price)) < 1e-6;
}

export function isRegistryDomainVariation(variationUuid?: string | null): boolean {
  return !!variationUuid && REGISTRY_DOMAIN_UUIDS.includes(variationUuid);
}

/** Valor anual integral, cobrança única: não divide e não mostra /mês. */
export function isOneTimeRegistryDomainItem(
  item: Pick<OrderRecurrencyPriceItem, 'variationUuid' | 'productUuid'>
): boolean {
  return (
    isRegistryDomainVariation(item.variationUuid) ||
    isRegistryDomainProductUuid(item.productUuid) ||
    isRegistryDomainProductUuid(item.variationUuid)
  );
}

function normalizeRecurrency(recurrency?: OrderPaymentRecurrency): string {
  return (recurrency ?? '').toLowerCase();
}

export function isMindPaymentMethod(paymentMethod?: string | null): boolean {
  return (paymentMethod ?? '').toUpperCase() === 'MIND';
}

/** True when listing/details API sent promo `extras.periods[0].price`. */
export function hasOrderExtrasPromo(extras?: MindOrderExtras | null): boolean {
  return (Number(extras?.periods?.[0]?.price) || 0) > 0;
}

/**
 * Mind/cents + promo rules apply for paymentMethod MIND
 * or whenever `extras` promo is present (any payment method).
 */
export function shouldApplyMindOrderPriceRules(
  paymentMethod?: string | null,
  extras?: MindOrderExtras | null
): boolean {
  return isMindPaymentMethod(paymentMethod) || hasOrderExtrasPromo(extras);
}

/**
 * createOrder Mind grava preço em centavos (ex.: 9990).
 * Pedidos legados podem já estar em reais (ex.: 94.9).
 */
export function mindStoredPriceToReais(price: number): number {
  const n = Number(price) || 0;
  if (isFreeOrderPrice(n)) {
    return 0;
  }
  if (Number.isInteger(n) && Math.abs(n) >= 100) {
    return n / 100;
  }
  return n;
}

/** @deprecated Prefer mindStoredPriceToReais (handles legacy reais). */
export function mindOrderCentsToReais(cents: number): number {
  return mindStoredPriceToReais(cents);
}

/**
 * Resolve UI price for a MIND order line using optional `extras` promo periods.
 * `extras.periods[].price` is always in cents; `validity` is days.
 */
export function resolveMindOrderListPrice(
  storedPrice: number,
  extras?: MindOrderExtras | null
): MindOrderListPriceUi {
  const originalPrice = mindStoredPriceToReais(storedPrice);
  const period = extras?.periods?.[0];
  const promoCents = Number(period?.price) || 0;
  const displayFromPromo = promoCents > 0 ? promoCents / 100 : 0;
  const hasPromotion =
    displayFromPromo > 0 && originalPrice > displayFromPromo + 1e-6;

  if (hasPromotion && period) {
    const validityDays = Number(period.validity) || 0;
    const promotionMonths = Math.max(1, Math.round(validityDays / 30));
    return {
      displayPrice: displayFromPromo,
      originalPrice,
      hasPromotion: true,
      promotionMonths,
      description: (extras?.description ?? '').toString().trim() || null,
    };
  }

  return {
    displayPrice: originalPrice,
    originalPrice,
    hasPromotion: false,
    promotionMonths: 0,
    description: null,
  };
}

/**
 * Só `monthly` divide por 12.
 * `annual` mantém o valor cheio (só recebe sufixo /ano).
 * Registro de domínio / MIND / exclusões legadas: não divide.
 */
export function shouldDivideOrderPriceBy12(
  item: OrderRecurrencyPriceItem,
  paymentMethod?: string | null,
  extras?: MindOrderExtras | null
): boolean {
  if (shouldApplyMindOrderPriceRules(paymentMethod, extras)) {
    return false;
  }
  if (isOneTimeRegistryDomainItem(item) || isFreeOrderPrice(item.price)) {
    return false;
  }

  const recurrency = normalizeRecurrency(item.paymentRecurrency);
  if (recurrency === 'monthly') {
    return true;
  }
  if (recurrency === 'annual' || recurrency) {
    return false;
  }

  const variationUuid = item.variationUuid;
  if (!variationUuid) {
    return true;
  }
  return !EXCLUDED_VARIATION_UUIDS.includes(variationUuid);
}

export function getOrderDisplayUnitPrice(
  item: OrderRecurrencyPriceItem,
  paymentMethod?: string | null,
  extras?: MindOrderExtras | null
): number {
  if (shouldApplyMindOrderPriceRules(paymentMethod, extras)) {
    return resolveMindOrderListPrice(item.price, extras).displayPrice;
  }
  return shouldDivideOrderPriceBy12(item, paymentMethod, extras)
    ? item.price / 12
    : item.price;
}

/** Suffix key segment: 'perMonth' | 'perYear' | null */
export function getOrderPriceSuffixKind(
  item: OrderRecurrencyPriceItem,
  paymentMethod?: string | null,
  extras?: MindOrderExtras | null
): 'perMonth' | 'perYear' | null {
  if (
    isFreeOrderPrice(item.price) ||
    shouldApplyMindOrderPriceRules(paymentMethod, extras)
  ) {
    return null;
  }
  if (isOneTimeRegistryDomainItem(item)) {
    return null;
  }

  const recurrency = normalizeRecurrency(item.paymentRecurrency);
  if (recurrency === 'annual') {
    return 'perYear';
  }
  if (recurrency === 'monthly') {
    return 'perMonth';
  }

  // legado sem paymentRecurrency: /mês quando divide
  if (shouldDivideOrderPriceBy12(item, paymentMethod, extras)) {
    return 'perMonth';
  }
  return null;
}

export function getOrderDisplayTotal(
  items: OrderRecurrencyPriceItem[],
  fallbackTotal: number,
  paymentMethod?: string | null,
  extras?: MindOrderExtras | null
): number {
  if (shouldApplyMindOrderPriceRules(paymentMethod, extras)) {
    if (!items.length) {
      return resolveMindOrderListPrice(fallbackTotal, extras).displayPrice;
    }
    return items.reduce((acc, item) => {
      if (isFreeOrderPrice(item.price)) {
        return acc;
      }
      const qty = item.quantity ?? 1;
      return (
        acc + resolveMindOrderListPrice(item.price, extras).displayPrice * qty
      );
    }, 0);
  }

  if (!items.length) {
    return fallbackTotal;
  }

  return items.reduce((acc, item) => {
    if (isFreeOrderPrice(item.price)) {
      return acc;
    }
    const qty = item.quantity ?? 1;
    return acc + getOrderDisplayUnitPrice(item, paymentMethod, extras) * qty;
  }, 0);
}

export function getOrderTotalSuffixKind(
  items: OrderRecurrencyPriceItem[],
  paymentMethod?: string | null,
  extras?: MindOrderExtras | null
): 'perMonth' | 'perYear' | null {
  if (shouldApplyMindOrderPriceRules(paymentMethod, extras)) {
    return null;
  }

  const priced = items.filter((item) => !isFreeOrderPrice(item.price));
  if (!priced.length) {
    return null;
  }

  const kinds = priced.map((item) =>
    getOrderPriceSuffixKind(item, paymentMethod, extras)
  );
  if (kinds.some((k) => k == null)) {
    return null;
  }
  const first = kinds[0];
  return kinds.every((k) => k === first) ? first : null;
}
